import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "../components/Reveal";
import { fetchProjects, fetchSettings } from "../lib/api";

const PORTRAIT_FALLBACK =
    "https://customer-assets.emergentagent.com/job_director-portfolio-11/artifacts/xib0uo3r_IMG_6156.jpeg";

const About = () => {
    const [portrait, setPortrait] = useState(PORTRAIT_FALLBACK);
    const [projects, setProjects] = useState([]);

    useEffect(() => {
        fetchSettings()
            .then((s) => {
                if (s?.portrait_url) setPortrait(s.portrait_url);
            })
            .catch(() => {});
        fetchProjects()
            .then(setProjects)
            .catch(() => {});
    }, []);

    // Major Credits = curated work that isn't SMOSH (SMOSH already lives in
    // the prestige card above). Featured-first, capped at 6 for editorial
    // density without overwhelming.
    const majorCredits = useMemo(() => {
        return projects
            .filter((p) => p.title?.toLowerCase() !== "smosh")
            .sort((a, b) => {
                if ((b.featured ? 1 : 0) !== (a.featured ? 1 : 0)) {
                    return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
                }
                return (b.year || 0) - (a.year || 0);
            })
            .slice(0, 6);
    }, [projects]);

    // Full Filmography = every project, sorted by year desc.
    const filmography = useMemo(
        () =>
            [...projects].sort(
                (a, b) => (b.year || 0) - (a.year || 0) || a.title.localeCompare(b.title),
            ),
        [projects],
    );

    return (
        <main
            data-testid="about-page"
            className="pt-32 sm:pt-40 pb-32 mx-auto max-w-[1500px] px-6 sm:px-10 lg:px-14"
        >
            <Reveal>
                <p className="text-label text-muted-foreground mb-6">About</p>
                <h1 className="font-serif-display text-5xl sm:text-7xl tracking-tighter leading-[1.02] max-w-4xl">
                    Two decades of <span className="italic text-muted-foreground">stories told in light.</span>
                </h1>
            </Reveal>

            <div className="mt-20 grid lg:grid-cols-12 gap-12">
                <Reveal className="lg:col-span-5">
                    <div className="overflow-hidden bg-muted aspect-square cinematic-shadow">
                        <img
                            src={portrait}
                            alt="John Alexander Jimenez"
                            data-testid="about-portrait"
                            className="w-full h-full object-cover"
                            loading="lazy"
                        />
                    </div>
                </Reveal>

                <Reveal delay={200} className="lg:col-span-7 lg:pt-2">
                    <p className="text-label text-muted-foreground mb-4">Bio</p>
                    <div className="space-y-6 text-lg leading-relaxed text-foreground/85 max-w-xl">
                        <p>
                            John Alexander Jimenez has been working in film and
                            video production for over two decades with great
                            success. His work in short films and commercials
                            has not only refined his craft but earned
                            recognition through multiple awards.
                        </p>
                        <p>
                            Among those are the&nbsp;
                            <span className="text-foreground italic">
                                2000 Sacramento Metropolitan Arts Commission
                                Emerging Artist Award
                            </span>{" "}
                            for cinematography, and the Sacramento Film
                            Festival's&nbsp;
                            <span className="text-foreground italic">
                                2014 Local Vanguard Award
                            </span>{" "}
                            for contributions to productions throughout the
                            Sacramento area.
                        </p>
                    </div>

                    <div className="mt-14">
                        <p className="text-label text-muted-foreground mb-6">
                            Philosophy
                        </p>
                        <blockquote className="font-serif-display text-2xl sm:text-3xl leading-snug tracking-tight max-w-xl">
                            "The right frame is rarely the loudest one. It is
                            the one that lets the story breathe."
                        </blockquote>
                    </div>

                    <div className="mt-14 grid sm:grid-cols-3 gap-8 max-w-2xl">
                        {[
                            ["20+", "Years on set"],
                            ["2", "Major awards"],
                            ["100+", "Productions lensed"],
                        ].map(([n, l]) => (
                            <div key={l}>
                                <p className="font-serif-display text-4xl sm:text-5xl tracking-tighter">
                                    {n}
                                </p>
                                <p className="mt-2 text-label text-muted-foreground">
                                    {l}
                                </p>
                            </div>
                        ))}
                    </div>
                </Reveal>
            </div>

            {/* ORIGINS — full-bleed black "archive plate" section */}
            <section
                data-testid="about-origins"
                className="relative left-1/2 -translate-x-1/2 w-screen mt-32 sm:mt-40 bg-black border-y border-border/40 overflow-hidden"
            >
                <div className="mx-auto max-w-[1500px] px-6 sm:px-10 lg:px-14 py-28 sm:py-40">
                    <Reveal>
                        <p className="text-[10px] tracking-[0.4em] uppercase text-foreground/45">
                            From the Archive · 35mm
                        </p>
                    </Reveal>

                    <Reveal delay={150}>
                        <h2
                            data-testid="origins-heading"
                            className="mt-10 font-serif-display text-6xl sm:text-8xl lg:text-9xl tracking-tighter leading-[0.95]"
                        >
                            Origins
                        </h2>
                    </Reveal>

                    <Reveal delay={320}>
                        <p
                            data-testid="origins-subheading"
                            className="mt-10 font-serif-display italic text-xl sm:text-2xl lg:text-3xl text-foreground/70 max-w-3xl leading-snug tracking-tight"
                        >
                            A career that began before instant playback.
                        </p>
                    </Reveal>

                    <div className="mt-24 sm:mt-32 grid lg:grid-cols-12 gap-14 lg:gap-20 items-start">
                        <Reveal className="lg:col-span-7">
                            <figure data-testid="origins-figure" className="relative">
                                <div className="relative overflow-hidden bg-neutral-950 border border-border/40 cinematic-shadow">
                                    <img
                                        src="https://customer-assets.emergentagent.com/job_director-portfolio-11/artifacts/6hg43b8f_IMG_1836.jpeg"
                                        alt="John Alexander Jimenez behind a 35mm Arriflex camera — early career, undated."
                                        loading="lazy"
                                        className="w-full h-auto object-cover"
                                    />
                                    {/* Subtle film grain — same SVG fractal-noise as the reel modal */}
                                    <div
                                        aria-hidden
                                        className="reel-grain"
                                        style={{ opacity: 0.05 }}
                                    />
                                </div>
                                <figcaption
                                    data-testid="origins-caption"
                                    className="mt-6 font-serif-display italic text-sm sm:text-base text-foreground/55 tracking-tight"
                                >
                                    "The cameras changed. The eye stayed the same."
                                </figcaption>
                            </figure>
                        </Reveal>

                        <Reveal delay={200} className="lg:col-span-5 lg:pt-10">
                            <p
                                data-testid="origins-body"
                                className="text-base sm:text-lg leading-loose text-foreground/75 max-w-md"
                            >
                                Before digital monitors, before unlimited takes,
                                before footage could be reviewed instantly —
                                there was film. Every decision mattered. And it
                                still does.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            <Reveal delay={150} className="mt-32 border-t border-border pt-16">
                <p className="text-label text-muted-foreground mb-6">
                    Selected Work
                </p>
                <h2 className="font-serif-display text-3xl sm:text-5xl tracking-tight max-w-3xl">
                    A few <span className="italic text-muted-foreground">defining</span> collaborations.
                </h2>

                <article
                    data-testid="selected-credit-smosh"
                    className="relative mt-16 border border-border bg-card/40 p-8 sm:p-12 lg:p-16 overflow-hidden group"
                >
                    {/* Background number flourish */}
                    <span
                        aria-hidden
                        className="pointer-events-none absolute -right-6 -bottom-12 font-serif-display text-[180px] sm:text-[240px] lg:text-[320px] leading-none tracking-tighter text-foreground/[0.04] select-none"
                    >
                        186
                    </span>

                    <div className="relative grid lg:grid-cols-12 gap-10 lg:gap-12">
                        <div className="lg:col-span-8">
                            <div
                                data-testid="smosh-badges"
                                className="flex flex-wrap gap-2 mb-8"
                            >
                                <span className="text-[10px] tracking-[0.28em] uppercase text-foreground/75 border border-foreground/30 px-3 py-1">
                                    Long-Running Series
                                </span>
                                <span className="text-[10px] tracking-[0.28em] uppercase text-foreground/75 border border-foreground/30 px-3 py-1">
                                    186 Episodes Shot
                                </span>
                            </div>

                            <h3
                                data-testid="smosh-title"
                                className="font-serif-display text-6xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.92]"
                            >
                                SMOSH
                            </h3>

                            <p className="mt-8 text-label text-foreground/75">
                                Director of Photography&nbsp;&nbsp;·&nbsp;&nbsp;2011–2017&nbsp;&nbsp;·&nbsp;&nbsp;186 Episodes
                            </p>
                            <p className="mt-2 text-sm text-muted-foreground tracking-wide">
                                Comedy Series — Digital Entertainment
                            </p>

                            <p className="mt-8 font-serif-display text-xl sm:text-2xl leading-snug tracking-tight text-foreground/85 max-w-xl">
                                "Served as Director of Photography across 186
                                episodes during a major growth era for one of
                                YouTube's most influential comedy brands —
                                balancing speed, performance, and cinematic
                                polish at high production volume."
                            </p>
                        </div>

                        <div className="lg:col-span-4 lg:pt-2">
                            <figure
                                data-testid="smosh-poster"
                                className="relative aspect-square overflow-hidden border border-border bg-muted cinematic-shadow max-w-[340px] lg:ml-auto"
                            >
                                <img
                                    src="https://customer-assets.emergentagent.com/job_director-portfolio-11/artifacts/8egizjll_IMG_6158.jpeg"
                                    alt="SMOSH key art"
                                    loading="lazy"
                                    className="w-full h-full object-cover"
                                    style={{
                                        filter:
                                            "saturate(0.78) contrast(1.05) brightness(0.92)",
                                    }}
                                />
                                <figcaption className="absolute bottom-0 inset-x-0 px-4 py-3 text-[10px] tracking-[0.28em] uppercase text-foreground/70 bg-gradient-to-t from-black/80 via-black/45 to-transparent">
                                    Key Art — SMOSH
                                </figcaption>
                            </figure>
                        </div>
                    </div>

                    {/* Metadata grid */}
                    <div className="relative mt-12 pt-8 border-t border-border grid grid-cols-2 sm:grid-cols-4 gap-y-8 gap-x-6">
                        {[
                            ["Role", "Director of Photography"],
                            ["Years", "2011–2017"],
                            ["Episodes", "186"],
                            ["Format", "Comedy Series / Digital"],
                        ].map(([k, v]) => (
                            <div key={k}>
                                <p className="text-label text-muted-foreground mb-3">
                                    {k}
                                </p>
                                <p className="font-serif-display text-lg sm:text-xl tracking-tight leading-snug">
                                    {v}
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* Expandable note */}
                    <details
                        data-testid="smosh-details"
                        className="relative mt-10 group/d"
                    >
                        <summary className="inline-flex items-center gap-3 cursor-pointer list-none text-label text-muted-foreground hover:text-foreground transition-colors duration-500">
                            <span>More on this collaboration</span>
                            <span
                                aria-hidden
                                className="text-base leading-none transition-transform duration-500 group-open/d:rotate-45"
                            >
                                +
                            </span>
                        </summary>
                        <p className="mt-6 text-base sm:text-lg text-foreground/75 leading-relaxed max-w-3xl">
                            Long-form collaboration requiring rapid turnaround,
                            evolving visual style, and consistency across years
                            of episodic production.
                        </p>
                    </details>
                </article>
            </Reveal>

            {/* MAJOR CREDITS — curated grid, populated from /api/projects */}
            <Reveal
                delay={150}
                className="mt-32 border-t border-border pt-20"
            >
                <p className="text-label text-muted-foreground mb-4">
                    Major Credits
                </p>
                <h2
                    data-testid="major-credits-heading"
                    className="font-serif-display text-3xl sm:text-5xl tracking-tight max-w-3xl mb-16"
                >
                    Selected work across{" "}
                    <span className="italic text-muted-foreground">
                        narrative, commercial, and documentary.
                    </span>
                </h2>

                {majorCredits.length === 0 ? (
                    <p
                        data-testid="major-credits-empty"
                        className="text-muted-foreground text-sm italic"
                    >
                        Credits are being archived. Check back soon.
                    </p>
                ) : (
                    <div
                        data-testid="major-credits-grid"
                        className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16"
                    >
                        {majorCredits.map((p) => (
                            <Link
                                key={p.id}
                                to={`/work/${p.id}`}
                                data-testid={`major-credit-${p.id}`}
                                className="group block"
                            >
                                <div className="aspect-[4/5] bg-muted overflow-hidden">
                                    {p.thumbnail_url ? (
                                        <img
                                            src={p.thumbnail_url}
                                            alt={p.title}
                                            loading="lazy"
                                            style={
                                                p.thumbnail_position
                                                    ? {
                                                          objectPosition:
                                                              p.thumbnail_position,
                                                      }
                                                    : undefined
                                            }
                                            className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                                        />
                                    ) : (
                                        <div className="w-full h-full" />
                                    )}
                                </div>
                                <div className="mt-5 flex items-baseline justify-between gap-4">
                                    <h3 className="font-serif-display text-xl tracking-tight">
                                        {p.title}
                                    </h3>
                                    <span className="text-label text-muted-foreground tabular-nums">
                                        {p.year}
                                    </span>
                                </div>
                                <p className="mt-1 text-sm text-muted-foreground tracking-wide">
                                    {p.role}
                                    {p.category ? ` — ${p.category}` : ""}
                                </p>
                            </Link>
                        ))}
                    </div>
                )}
            </Reveal>

            {/* FULL FILMOGRAPHY — comprehensive editorial list */}
            <Reveal
                delay={150}
                className="mt-32 border-t border-border pt-20"
            >
                <div className="flex items-baseline justify-between gap-6 mb-16 flex-wrap">
                    <div>
                        <p className="text-label text-muted-foreground mb-4">
                            Full Filmography
                        </p>
                        <h2
                            data-testid="filmography-heading"
                            className="font-serif-display text-3xl sm:text-5xl tracking-tight max-w-3xl"
                        >
                            Every project, by year.
                        </h2>
                    </div>
                    <p
                        data-testid="filmography-count"
                        className="text-[10px] tracking-[0.32em] uppercase text-muted-foreground tabular-nums"
                    >
                        {filmography.length} entries
                    </p>
                </div>

                {filmography.length === 0 ? (
                    <p
                        data-testid="filmography-empty"
                        className="text-muted-foreground text-sm italic"
                    >
                        Filmography is being archived.
                    </p>
                ) : (
                    <ul
                        data-testid="filmography-list"
                        className="divide-y divide-border border-y border-border"
                    >
                        {filmography.map((p) => (
                            <li
                                key={p.id}
                                data-testid={`filmography-row-${p.id}`}
                            >
                                <Link
                                    to={`/work/${p.id}`}
                                    className="group grid grid-cols-[64px_1fr_auto] sm:grid-cols-[100px_minmax(0,1.4fr)_minmax(0,1fr)_auto] gap-4 sm:gap-8 items-baseline py-6 px-2 -mx-2 hover:bg-foreground/[0.025] transition-colors duration-500"
                                >
                                    <span className="text-label text-muted-foreground tabular-nums">
                                        {p.year || "—"}
                                    </span>
                                    <span className="font-serif-display text-lg sm:text-2xl tracking-tight text-foreground/90 group-hover:text-foreground transition-colors truncate">
                                        {p.title}
                                    </span>
                                    <span className="hidden sm:block text-sm text-muted-foreground tracking-wide truncate">
                                        {p.role}
                                    </span>
                                    <span className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground whitespace-nowrap">
                                        {p.category}
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </Reveal>
        </main>
    );
};

export default About;
